<script setup lang="ts">
import { useDisplay } from 'vuetify'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import i18n from '@/plugins/i18n'
import { useRouter } from 'vue-router'
import * as Sentry from '@sentry/vue'

const { mdAndUp, smAndDown } = useDisplay()
const { t } = i18n.global

const appStore = useAppStore()
const authStore = useAuthStore()
const router = useRouter()

const returnToMyCS = () => {
  window.location.replace(`${ appStore.config.MYCS_BASE_URL }/`)
}

const returnToMyPP = () => {
  window.location.replace(`${ appStore.config.MYPP_BASE_URL }/`)
}

const contactSupport = () => {
  window.location.replace(`${ appStore.config.AUTH_BASE_URL }/enquiry?app=${ appStore.config.APP_NAME }&return_url=${ window.location.href }`)
}

const myDetails = () => {
  window.location.replace(`${ appStore.config.AUTH_BASE_URL }/my-details?app=${ appStore.config.APP_NAME }&return_url=${ window.location.href }`)
}

const myfjTrial = () => {
  window.location.replace(`${appStore.config.MYFJ_BASE_URL}/auth/trial-student`)
}

const profileSwitchUrl = (userProfileId: number) => {
  localStorage.removeItem('selected_interviewing')
  window.location.replace(`${ appStore.config.MYHQ_BASE_URL }/login-as/${userProfileId}?return_url=${ window.location.protocol + '//' + window.location.host }`)
}

const selectInterviewing = () => {
  authStore.setSelectedInterviewing(true)
  router.push({ name: 'interviewer-days.list' })
}

const userLogout = () => {
  authStore.logout()
  window.location.replace(`${appStore.config.AUTH_BASE_URL}/logout`)
}

const feedback = Sentry.feedbackIntegration({
  formTitle: t('user_feedback.header'),
  messagePlaceholder: t('user_feedback.message_placeholder'),
  submitButtonLabel: t('user_feedback.submit_button'),
})

const onFeedbackSubmit = async () => {
  const form = await feedback.createForm()

  form.appendToDom()
  form.open()
}
</script>

<template>
  <div class="d-flex align-center">
    <div
      v-if="smAndDown"
      class="d-flex mr-2"
    >
      <VAvatar
        color="primary"
        variant="tonal"
      >
        <VImg
          :src="authStore.organisationLogo?.src"
        />
      </VAvatar>
    </div>

    <div class="d-flex">
      <VAvatar
        class="cursor-pointer"
        color="primary"
        variant="tonal"
      >
        <VIcon icon="mdi-account" />
      </VAvatar>

      <div
        v-if="mdAndUp"
        class="d-flex align-center pl-2 cursor-pointer user-name-with-org"
      >
        <div class="d-flex flex-column">
          <span class="text-body-1">{{ authStore.user.name }}</span>
          <span
            v-if="authStore.user.menu_name"
            class="text-caption text-medium-emphasis"
          >{{ authStore.user.menu_name }}</span>
        </div>
        <VIcon icon="mdi-menu-down" />
      </div>

      <!-- SECTION Menu -->
      <VMenu
        activator="parent"
        width="400"
        location="bottom end"
        offset="14px"
      >
        <VList>
          <!-- 👉 User Avatar & Name -->
          <VListItem>
            <template #prepend>
              <VListItemAction start>
                <VAvatar
                  color="primary"
                  variant="tonal"
                  icon="mdi-account"
                />
              </VListItemAction>
            </template>

            <VListItemTitle class="font-weight-semibold">
              {{ authStore.user.name }}
            </VListItemTitle>
            <VListItemSubtitle>
              {{ authStore.organisation?.name }}
            </VListItemSubtitle>
          </VListItem>

          <VDivider class="my-2" />

          <!-- 👉 Return to MyCS -->
          <VListItem
            v-if="authStore.user_type === 'admin'"
            @click="returnToMyCS"
          >
            <template #prepend>
              <VIcon
                class="me-2"
                icon="mdi-keyboard-return"
                size="22"
              />
            </template>

            <VListItemTitle v-dompurify-html="i18n.global.t('header.return_to_mycs')" />
          </VListItem>

          <!-- 👉 Return to MyPP -->
          <VListItem
            v-if="authStore.user_profile?.type === 'business_partner'"
            @click="returnToMyPP"
          >
            <template #prepend>
              <VIcon
                class="me-2"
                icon="mdi-keyboard-return"
                size="22"
              />
            </template>

            <VListItemTitle v-dompurify-html="i18n.global.t('header.return_to_mypp')" />
          </VListItem>

          <!-- 👉 Trial Student -->
          <VListItem
            v-if="authStore.user.has_trial_student"
            @click="myfjTrial"
          >
            <template #prepend>
              <VIcon
                class="me-2"
                icon="mdi-login"
                size="22"
              />
            </template>

            <VListItemTitle>{{ i18n.global.t('header.myfj_trial') }}</VListItemTitle>
          </VListItem>

          <!-- 👉 Careers Meetings -->
          <template v-if="authStore.is_interviewer && !authStore.selected_interviewing">
            <VDivider class="my-2" />
            <VListItem
              @click="selectInterviewing"
            >
              <template #prepend>
                <VIcon
                  class="me-2"
                  icon="mdi-account-supervisor"
                  size="22"
                />
              </template>
              <VListItemTitle v-dompurify-html="i18n.global.t('header.switch_to_careers_meetings')" />
            </VListItem>
          </template>

          <template v-if="!authStore.selected_interviewing && authStore.displayUserProfileSelector">
            <VDivider class="my-2" />
            <template
              v-for="altUserProfile in authStore.user_profiles"
              :key="altUserProfile.id"
            >
              <VListItem
                v-if="authStore.user_profile?.id != altUserProfile.id && !altUserProfile.organisation.is_business_partner"
                @click="profileSwitchUrl(altUserProfile.id)"
              >
                <template #prepend>
                  <VIcon
                    class="me-2"
                    icon="mdi-swap-horizontal"
                    size="22"
                  />
                </template>

                <VListItemTitle
                  v-dompurify-html="i18n.global.t('header.switch_to_org', { orgName: altUserProfile.organisation.name })"
                  class="dropdown-item"
                />
              </VListItem>
            </template>
          </template>

          <VDivider class="my-2" />
          <!-- 👉 Support -->
          <VListItem @click="contactSupport">
            <template #prepend>
              <VIcon
                class="me-2"
                icon="mdi-account-question"
                size="22"
              />
            </template>

            <VListItemTitle>{{ i18n.global.t('header.contact_support') }}</VListItemTitle>
          </VListItem>

          <!-- 👉 Profile -->
          <VListItem @click="myDetails">
            <template #prepend>
              <VIcon
                class="me-2"
                icon="mdi-account"
                size="22"
              />
            </template>

            <VListItemTitle>{{ i18n.global.t('header.my_details') }}</VListItemTitle>
          </VListItem>

          <!-- 👉 User Feedback -->
          <VListItem @click="onFeedbackSubmit">
            <template #prepend>
              <VIcon
                class="me-2"
                icon="mdi-message-alert"
                size="22"
              />
            </template>

            <VListItemTitle>{{ i18n.global.t('header.submit_feedback') }}</VListItemTitle>
          </VListItem>

          <!-- Divider -->
          <VDivider class="my-2" />

          <!-- 👉 Logout -->
          <VListItem @click="userLogout">
            <template #prepend>
              <VIcon
                class="me-2"
                icon="mdi-logout"
                size="22"
              />
            </template>

            <VListItemTitle>{{ i18n.global.t('header.logout') }}</VListItemTitle>
          </VListItem>
        </VList>
      </VMenu>
    </div>
    <!-- !SECTION -->
  </div>
</template>

<style lang="scss" scoped>
.dropdown-item {
  white-space: normal;
  text-overflow: unset;
}
.user-name-with-org {
  @media (min-width: 1280px) and (max-width: 1430px) {
      display: none !important;
  }
}
</style>
